import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiHandler } from '../../api/apihandler';
import { RELATED_RECIPES } from '../../api/constants';
import { apiSucceeded } from '../../views/dashboard/feed/feedUtils';
import MediaPlaceholderIcon from './MediaPlaceholderIcon';

const MAX_RESULTS = 6;

// Right-rail "Related Recipes" list for the recipe detail screen, backed by
// the dedicated users/v1/feed/related-recipes endpoint (server-side
// matching by recipe_id, no client-side cuisine filtering needed).
const RelatedRecipesWidget = ({ currentId }) => {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apiHandler(RELATED_RECIPES, 'POST', { page: 1, limit: MAX_RESULTS, recipe_id: currentId });
        if (!cancelled && apiSucceeded(res)) {
          const raw = res?.data?.data?.recipes?.data || [];
          const mapped = raw.map((d) => {
            const firstMedia = d.user_post_images?.[0] || null;
            const isVideo = firstMedia?.media_type === 1;
            const image = firstMedia ? (isVideo ? (firstMedia.thumb || null) : firstMedia.image_path) : null;
            return {
              id: d.id,
              title: d.title || 'Untitled recipe',
              image,
              isVideo,
              author: `${d.user?.first_name || ''} ${d.user?.last_name || ''}`.trim() || 'User',
              cookTime: d.cook_time || 0,
              cuisineName: d.cuisine_type?.cuisine?.name || null,
            };
          });
          setRecipes(mapped.slice(0, MAX_RESULTS));
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentId]);

  if (!loading && recipes.length === 0) return null;

  return (
    <div className="nmha-rail-block">
      <div className="nmha-rail-head"><h3>Related Recipes</h3></div>
      {loading ? (
        <p className="nmha-empty-text">Loading…</p>
      ) : (
        recipes.map((r) => (
          <div
            key={r.id}
            className="nmha-related-recipe-card"
            onClick={() => navigate(`/pages/recipe/${r.id}`)}
            role="button"
            tabIndex={0}
          >
            <div className="nmha-related-recipe-thumb">
              {r.image ? <img src={r.image} alt="" /> : <MediaPlaceholderIcon size={44} />}
              {r.isVideo && <span className="nmha-related-recipe-play">▶</span>}
              {r.cookTime > 0 && <span className="nmha-related-recipe-badge">⏱ {r.cookTime} min</span>}
            </div>
            <div className="nmha-related-recipe-title">{r.title}</div>
            <div className="nmha-related-recipe-author">By {r.author}</div>
            {r.cuisineName && <div className="nmha-related-recipe-meta">{r.cuisineName}</div>}
          </div>
        ))
      )}
    </div>
  );
};

export default RelatedRecipesWidget;
